import { Lock, ShieldCheck, FileCheck2, BadgeCheck } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

const badges = [
  {
    icon: Lock,
    label: "TLS 1.3 in transit",
    short: "Encrypted in transit",
    tooltip:
      "Every connection to FreeCRM is encrypted with TLS 1.3 — the latest transport layer security standard — so data exchanged between your browser and our servers cannot be read or tampered with in flight.",
  },
  {
    icon: ShieldCheck,
    label: "AES-256 at rest",
    short: "Encrypted at rest",
    tooltip:
      "All customer data stored in our databases and backups is encrypted at rest using AES-256, the same symmetric cipher used to protect classified information in regulated industries.",
  },
  {
    icon: FileCheck2,
    label: "SOC 2 Type II",
    short: "Independently audited",
    tooltip:
      "Our infrastructure is hosted in SOC 2 Type II audited data centers. An independent auditor verifies, on an ongoing basis, that controls for security, availability, and confidentiality operate effectively over time.",
  },
  {
    icon: BadgeCheck,
    label: "GDPR & CCPA ready",
    short: "Privacy-first by default",
    tooltip:
      "FreeCRM supports GDPR and CCPA requirements with a signed DPA, data export and deletion tools, regional data residency on Enterprise, and granular role-based access controls.",
  },
];

const TrustBadges = () => {
  return (
    <section
      id="security"
      aria-labelledby="trust-heading"
      className="border-y border-border bg-card/40 py-16"
    >
      <div className="container">
        <div className="mx-auto max-w-2xl text-center">
          <span className="text-sm font-semibold uppercase tracking-widest text-primary">
            Verified claims
          </span>
          <h2
            id="trust-heading"
            className="mt-3 font-display text-3xl font-bold text-secondary md:text-4xl"
          >
            Security you can verify, not just adjectives
          </h2>
          <p className="mt-3 text-muted-foreground">
            Hover any badge to see exactly what it means. No “military-grade” marketing —
            just the specific standards we’re held to.
          </p>
        </div>

        <ul className="mx-auto mt-10 grid max-w-5xl gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {badges.map(({ icon: Icon, label, short, tooltip }) => (
            <li key={label}>
              <Tooltip delayDuration={150}>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    aria-label={`${label}: ${tooltip}`}
                    className="group flex h-full w-full flex-col items-center gap-3 rounded-2xl border border-border bg-card p-6 text-center shadow-card transition-all hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-elegant focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                  >
                    <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                      <Icon className="h-6 w-6" aria-hidden="true" />
                    </span>
                    <span className="font-display text-base font-bold text-secondary">
                      {label}
                    </span>
                    <span className="text-xs uppercase tracking-wide text-muted-foreground">
                      {short}
                    </span>
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs text-sm leading-relaxed">
                  {tooltip}
                </TooltipContent>
              </Tooltip>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
};

export default TrustBadges;
